import * as _ from 'lodash';
import { IMapLayerProviderFormModel } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderFormModel';
import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';
import { ImageryProviderParameter, ImsImageryProvider } from 'app/config/ImsImageryProvider';
import { IMapLayerProviderPersister } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderPersister';
import { THIS_FIELD_IS_REQUIRED } from 'app/overview/config/configuration_tab/general/frontend_config/common/layer_provider_constants';
import {
    ImageryProviderParameterType,
    LayerParameterDefinition,
    MapLayerOptionsHelper,
} from 'app/overview/config/data/MapLayerOptionsHelper';
import OpenGisLookupService from 'app/shared/utilities/opengis_lookup.service';
import { buildUrl } from 'app/shared/BaseImageryLayersService';
import { isValidRequiredString } from 'app/form_config/components/string_input';

const DUPLICATE_PARAM_NAME = 'Parameter must be unique';

interface ImageryProviderParameterUnderEdit extends ImageryProviderParameter {
    errMsg: string;
    displayName: string;
    childProps?: any;
    isHidden?: boolean;
}

interface ImageryProviderCustomParameterUnderEdit extends ImageryProviderParameterUnderEdit {
    keyErrMsg: string; // err msg for the custom parameter's key
    errMsg: string; // err msg for the value
}

interface ImageryProviderFormViewState {
    expanded: boolean;
    isDirty: boolean;
    isValid: boolean;

    // Super mandatory fields
    name: string;
    nameErrMsg: string;

    // e.g. tms
    type: string;
    relativeUrl: boolean;
    hideInMapLayers: boolean;
    showByDefault: boolean;

    showProxyPrefix: boolean;
    proxyPrefix: string;
    proxyPrefixErrMsg: string;

    uuid: string;

    availableTypes: any[];

    availableOptionalParameters: LayerParameterDefinition[];
    // true if there is 1 or more available optional params available
    hasAvailableOptionalParameters: boolean;

    // holds the state of the optional param add drop down
    optionalParameterKeyToAdd: string;

    requiredParameters: ImageryProviderParameterUnderEdit[];
    optionalParameters: ImageryProviderParameterUnderEdit[];
    customParameters: ImageryProviderCustomParameterUnderEdit[];
}

export class ImageryProviderFormModel
    implements
        IFormFieldModel<ImageryProviderFormViewState>,
        IMapLayerProviderFormModel<ImsImageryProvider>
{
    // The form expanded state
    private _expanded: boolean = false;
    private _showProxyPrefix: boolean = false;

    private _valueUnderEdit: ImsImageryProvider;
    private _origValue: ImsImageryProvider;
    private _persister: IMapLayerProviderPersister;
    private _uuid: string;

    private _optionalParameterKeyToAdd: string;

    private _imagMapOptionsHelper: MapLayerOptionsHelper;

    private _availableTypes;

    private _openGISLookupService: OpenGisLookupService;

    /**
     *
     * @param {IMapLayerProviderPersister} persister
     * @param {ImsImageryProvider} origValue
     * @param {ImsImageryProvider} valueUnderEdit
     * @param {MapLayerOptionsHelper} imagMapOptionsHelper Used for testing
     * @param openGISLookupService Used for testing
     */
    constructor(
        persister: IMapLayerProviderPersister,
        origValue: ImsImageryProvider,
        valueUnderEdit: ImsImageryProvider,
        imagMapOptionsHelper: MapLayerOptionsHelper,
        openGISLookupService: OpenGisLookupService = null,
    ) {
        if (!imagMapOptionsHelper) {
            throw new Error(`imagMapOptionsHelper must be truthy`);
        }
        this._imagMapOptionsHelper = imagMapOptionsHelper;

        if (openGISLookupService === null) {
            openGISLookupService = new OpenGisLookupService();
        }
        this._openGISLookupService = openGISLookupService;

        // Calculated once at object creation
        this._availableTypes = Object.entries(this._imagMapOptionsHelper.getAvailableTypes()).map(
            (tuple) => {
                return {
                    value: tuple[0],
                    display: tuple[1].pretty,
                };
            },
        );

        this._uuid = valueUnderEdit.uuid;

        this._persister = persister;
        // orig value may be null
        this._origValue = origValue;
        this._valueUnderEdit = valueUnderEdit;

        this._init();
    }

    get valueUnderEdit(): ImsImageryProvider {
        return this._valueUnderEdit.clone();
    }

    destroy(): void {}

    getId(): string {
        return this._uuid;
    }

    get uuid(): string {
        return this._uuid;
    }

    getViewState(): ImageryProviderFormViewState {
        const nameErrMsg = !isValidRequiredString(this._valueUnderEdit.name)
            ? THIS_FIELD_IS_REQUIRED
            : null;
        const proxyPrefixErrMsg =
            this._showProxyPrefix && !isValidRequiredString(this._valueUnderEdit.proxyPrefixUrl)
                ? THIS_FIELD_IS_REQUIRED
                : null;

        const type = this._valueUnderEdit.type;

        const requiredOptions = this._imagMapOptionsHelper.getRequiredParams(type);
        const optionalOptions = this._imagMapOptionsHelper.getOptionalParams(type);

        const decoratedRequiredParams = convertParametersToViewRepresentation(
            type,
            this._valueUnderEdit.requiredParameters,
            requiredOptions,
        );

        const decoratedOptionalParams = convertParametersToViewRepresentation(
            type,
            this._valueUnderEdit.optionalParameters,
            optionalOptions,
        );

        const decoratedCustomParams = convertCustomParametersToViewRepresentation(
            type,
            this._valueUnderEdit.customParameters,
            [],
        );

        // Finally check is valid...
        const isValid = [nameErrMsg, proxyPrefixErrMsg]
            .concat(decoratedRequiredParams.map((p) => p.errMsg))
            .concat(decoratedOptionalParams.map((p) => p.errMsg))
            .concat(decoratedCustomParams.map((p) => p.errMsg))
            .concat(decoratedCustomParams.map((p) => p.keyErrMsg))
            .every((s) => s === null);

        const availableOptionalParameters = this._getAvailableOptionalParameters();

        return {
            expanded: this._expanded,
            isDirty: this._calculateIfDirty(),
            isValid,
            showProxyPrefix: this._showProxyPrefix,
            proxyPrefix: this._valueUnderEdit.proxyPrefixUrl,
            proxyPrefixErrMsg,
            name: this._valueUnderEdit.name,
            nameErrMsg: nameErrMsg,
            type,
            hideInMapLayers: this._valueUnderEdit.hideInMapLayers,
            showByDefault: this._valueUnderEdit.showByDefault,
            relativeUrl: this._valueUnderEdit.relativeUrl,
            uuid: this._valueUnderEdit.uuid,
            requiredParameters: decoratedRequiredParams,
            optionalParameters: decoratedOptionalParams,
            customParameters: decoratedCustomParams,
            availableTypes: this._availableTypes,
            availableOptionalParameters,
            hasAvailableOptionalParameters: availableOptionalParameters.length > 0,
            optionalParameterKeyToAdd: this._optionalParameterKeyToAdd,
        };
    }

    private _calculateIfDirty(): boolean {
        if (!this._origValue) {
            return true;
        }
        if (this._origValue.isDiff(this._valueUnderEdit)) {
            return true;
        }
        if (
            this._origValue.proxyPrefixButtonEnabled !==
            this._valueUnderEdit.proxyPrefixButtonEnabled
        ) {
            return true;
        }
        return false;
    }

    _getAvailableOptionalParameters(): LayerParameterDefinition[] {
        const currentOptionalParameterKeys = this._valueUnderEdit.optionalParameters.map(
            (p) => p.key,
        );
        const optionalOptions = this._imagMapOptionsHelper.getOptionalParams(
            this._valueUnderEdit.type,
        );
        return optionalOptions.filter((o) => !currentOptionalParameterKeys.includes(o.mapParam));
    }

    isDirty(): boolean {
        // Not the most efficient but eh...it's correct!
        return this.getViewState().isDirty;
    }

    isValid(): boolean {
        // Not the most efficient but eh...it's correct!
        return this.getViewState().isValid;
    }

    delete(): void {
        this._persister.deleteProviderForm(this._uuid);
    }

    revert(): void {
        if (this._origValue) {
            this._valueUnderEdit = this._origValue.clone();
            this._init();
            this._persister.providerFormUpdated();
        }
    }

    save(): Promise<any> {
        throw new Error('not implemented');
    }

    setName(name: string) {
        this._valueUnderEdit.name = name;
        this._persister.providerFormUpdated();
    }

    setHideInMapLayers(val: boolean) {
        this._valueUnderEdit.hideInMapLayers = val ? true : false;
        this._persister.providerFormUpdated();
    }

    setShowByDefault(val: boolean) {
        this._valueUnderEdit.showByDefault = val;
        this._persister.providerFormUpdated();
    }

    setType(type: string) {
        this._valueUnderEdit.type = type;
        this._resetOptionalParameterKeyToAdd();
        this._persister.providerFormUpdated();
    }

    setRelativeUrl(val: boolean) {
        this._valueUnderEdit.relativeUrl = val;
        this._persister.providerFormUpdated();
    }

    setShowProxyPrefix(val: boolean) {
        this._showProxyPrefix = val;
        this._valueUnderEdit.proxyPrefixButtonEnabled = val;
        this._persister.providerFormUpdated();
    }

    setProxyPrefix(prefix: string) {
        this._valueUnderEdit.proxyPrefixUrl = prefix;
        this._persister.providerFormUpdated();
    }

    setExpanded(val: boolean) {
        this._expanded = val;
        this._persister.providerFormUpdated();
    }

    setOptionalParameterKeyToAdd(val: string) {
        this._optionalParameterKeyToAdd = val;
        this._persister.providerFormUpdated();
    }

    _resetOptionalParameterKeyToAdd(): void {
        const available = this._getAvailableOptionalParameters();
        if (available.length > 0) {
            this._optionalParameterKeyToAdd = available[0].mapParam;
        } else {
            this._optionalParameterKeyToAdd = '';
        }
    }

    _loadCapabilities(): void {
        this._valueUnderEdit.requiredParameters.forEach((param) => {
            if (
                [
                    ImageryProviderParameterType.WMS_URL,
                    ImageryProviderParameterType.WMTS_URL,
                ].includes(param.type)
            ) {
                this.setStdParameterValue(param.key, param.value);
            }
        });
    }

    /**
     * Set one of the dynamic parameter values...
     *
     * To be used for required and optional fields only
     *
     * @param {string} key
     * @param value
     */
    setStdParameterValue(key: string, value: any, options: any = null) {
        const param = this._valueUnderEdit.getStdParameterByKey(key);

        // The symmetric operation found in convertParametersToViewRepresentation
        if (param.type === ImageryProviderParameterType.ARRAY) {
            // Convert the string from the form into an array
            value = value.toString();
            value = value.split(',');
        } else if (param.type === ImageryProviderParameterType.NUMBER) {
            if (isNaN(value)) {
                value = null;
            }
        } else if (
            [ImageryProviderParameterType.WMS_URL, ImageryProviderParameterType.WMTS_URL].includes(
                param.type,
            )
        ) {
            // If it's a relative URL, make sure we append our current window location origin
            const url = this.valueUnderEdit.relativeUrl ? buildUrl(value) : value;
            const service = this._openGISLookupService.getByURL(url);

            const currentLayers = this._valueUnderEdit.getStdParameterByKey(
                ImageryProviderParameterType.LAYERS,
            );

            if (service) {
                this._valueUnderEdit.service = service;

                // Try catch here in case service.load throws an exception
                try {
                    service
                        .load()
                        .then(() => {
                            // Grab the current layers so they will be reselected
                            if (service.hasDataError) {
                                console.error('OpenGisLookupService data not loaded');
                                this.setLayerDataUnavailable(currentLayers);
                            } else {
                                // Pass in the current layers (if any) as the values, and the capabilities layers
                                // as the dropdown options
                                this.setStdParameterValue(
                                    ImageryProviderParameterType.LAYERS,
                                    currentLayers ? currentLayers.value : null,
                                    {
                                        limit: service.layerLimit,
                                        limitError: `Layer limit for this URL is ${service.layerLimit}`,
                                        options: service.layers,
                                        noOptionsMessage: 'No options. ',
                                        isCreatable: false,
                                    },
                                );

                                this._valueUnderEdit.optionalParameters.forEach((param) => {
                                    if (service && service.hasOptionalParameter(param.key)) {
                                        this._valueUnderEdit.removeOptionalParameter(param.key);
                                        this._valueUnderEdit.addOptionalParameter(
                                            param.key,
                                            param.value,
                                        );
                                    }
                                });
                            }
                        })
                        .catch((error) => {
                            console.error('OpenGisLookupService load failed', error);
                            this.setLayerDataUnavailable(currentLayers);
                        });
                } catch (e) {
                    console.error('Unexpected error occurred while loading OpenGisLookupService ');
                    console.error(e);
                    this.setLayerDataUnavailable(currentLayers);
                }
            } else {
                console.error('OpenGisLookupService could not be initialised');
                this.setLayerDataUnavailable(currentLayers);
            }
        }

        this._valueUnderEdit.setStdParameterValue(key, value, options);
        this._persister.providerFormUpdated();
    }

    private setLayerDataUnavailable(currentLayers: ImageryProviderParameter) {
        this.setStdParameterValue(
            ImageryProviderParameterType.LAYERS,
            currentLayers ? currentLayers.value : null,
            {
                noOptionsMessage: 'Layers List Unavailable. ',
                isCreatable: true,
                options: [],
            },
        );
    }

    /**
     * More for testing than anything else...
     * @param {string} key
     */
    getParameterValue(key: string): any {
        return this._valueUnderEdit.getParameterValue(key);
    }

    addOptionalParameter(key: string): void {
        this._valueUnderEdit.addOptionalParameter(key);
        this._resetOptionalParameterKeyToAdd();
        this._persister.providerFormUpdated();
    }

    /**
     * TODO - delete, this is unused?!
     * @param {string} key
     * @param {string} newKey
     */
    setOptionalParameterKey(key: string, newKey: string): void {
        this._valueUnderEdit.setOptionalParameterKey(key, newKey);
        this._persister.providerFormUpdated();
    }

    removeOptionalParameter(key: string): void {
        this._valueUnderEdit.removeOptionalParameter(key);
        this._resetOptionalParameterKeyToAdd();
        this._persister.providerFormUpdated();
    }

    /**
     * The desired key, e.g. 'my new param name'
     *
     * After creation the custom parameter is always referenced by its
     * field Id
     *
     * @param {string} key
     */
    addCustomParameter(key: string): void {
        this._valueUnderEdit.addCustomParameter(key);
        this._persister.providerFormUpdated();
    }

    setCustomParameterValue(fieldId: string, value: any) {
        this._valueUnderEdit.setCustomParameterValue(fieldId, value);
        this._persister.providerFormUpdated();
    }

    setCustomParameterKey(fieldId: string, key: string) {
        this._valueUnderEdit.setCustomParameterKey(fieldId, key);
        this._persister.providerFormUpdated();
    }

    removeCustomParameter(fieldId: string): void {
        this._valueUnderEdit.removeCustomParameter(fieldId);
        this._persister.providerFormUpdated();
    }

    updateOrigValue(value: ImsImageryProvider) {
        if (!this.isDirty()) {
            this._origValue = value;
            this.revert();
        } else {
            this._origValue = value;
        }
    }

    private _init(): void {
        this._loadCapabilities();
        this._resetOptionalParameterKeyToAdd();
        this._showProxyPrefix = isValidRequiredString(this._valueUnderEdit.proxyPrefixUrl);
    }
}

/**
 * Decorate the ImsProviderParameter with additional information
 * for rendering
 * @param {string} type the current imagery type, e.g. tms, of the form
 * @param {ImageryProviderParameter[]} params
 * @param layerParamDefs
 * @returns {ImageryProviderParameterUnderEdit[]}
 */
function convertParametersToViewRepresentation(
    type: string,
    params: ImageryProviderParameter[],
    layerParamDefs: LayerParameterDefinition[],
): ImageryProviderParameterUnderEdit[] {
    return params.map((p) => {
        const errMsg = !isValidRequiredString(p.value?.toString()) ? THIS_FIELD_IS_REQUIRED : null;
        let value = p.value;

        // Handle special array case
        // The symmetric operation found in ImsProviderFormModel.setStdParameterValue()
        if (p.type === ImageryProviderParameterType.ARRAY) {
            value = ((p.value as []) || []).join(',');
        }

        const layerParamDef = layerParamDefs.find((o) => o.mapParam === p.key);

        // childProps
        let childProps: any = null;
        if (p.childProps && layerParamDef && layerParamDef.childProps) {
            childProps = Object.assign({}, p.childProps, layerParamDef.childProps);
        } else if (p.childProps) {
            childProps = p.childProps;
        } else if (layerParamDef && layerParamDef.childProps) {
            childProps = layerParamDef.childProps;
        }

        return Object.assign({}, p, {
            errMsg,
            displayName: layerParamDef ? layerParamDef.prettyParam : p.key,
            value,
            childProps,
        });
    });
}

/**
 * Custom parameters are special case
 * 1. They are always strings
 * 2. The user edits the key so we need to do special validation on that
 *
 * @param {string} type
 * @param {ImageryProviderParameter[]} params
 * @param {LayerParameterDefinition[]} layerParamDefs
 * @returns {ImageryProviderCustomParameterUnderEdit[]}
 */
function convertCustomParametersToViewRepresentation(
    type: string,
    params: ImageryProviderParameter[],
    layerParamDefs: LayerParameterDefinition[],
): ImageryProviderCustomParameterUnderEdit[] {
    return params.map((p) => {
        const errMsg =
            _.isNil(p.value) || !isValidRequiredString(p.value.toString())
                ? THIS_FIELD_IS_REQUIRED
                : null;

        let keyErrMsg = null;
        if (!isValidRequiredString(p.key.toString())) {
            keyErrMsg = THIS_FIELD_IS_REQUIRED;
        }

        if (!keyErrMsg && countKeyOccurrences(p.key, params) > 1) {
            keyErrMsg = DUPLICATE_PARAM_NAME;
        }

        let value = p.value;

        // Handle special array case
        // The symmetric operation found in ImsProviderFormModel.setStdParameterValue()
        if (p.type === ImageryProviderParameterType.ARRAY) {
            value = ((p.value as []) || []).join(',');
        }

        const layerParamDef = layerParamDefs.find((o) => o.mapParam === p.key);
        return Object.assign({}, p, {
            errMsg,
            keyErrMsg,
            displayName: layerParamDef ? layerParamDef.prettyParam : p.key,
            value: value,
        });
    });
}

function countKeyOccurrences(key: string, params: ImageryProviderParameter[]) {
    return params.reduce((result, p) => {
        if (p.key === key) {
            return result + 1;
        }
        return result;
    }, 0);
}
